import { Link } from 'react-router-dom';

interface IGHeroProps {
  onCtaClick?: () => void;
}

export default function IGHero({ onCtaClick }: IGHeroProps) {
  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center px-5 pt-20 pb-16 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0d0c09] via-[#111008] to-[#0a0908]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,_rgba(201,164,64,0.10)_0%,_transparent_55%)]" />
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-luxury-black to-transparent" />

      {/* Partículas decorativas */}
      <div className="absolute top-24 left-6 w-1 h-1 rounded-full bg-gold-400/40" />
      <div className="absolute top-40 right-10 w-1.5 h-1.5 rounded-full bg-gold-400/25" />
      <div className="absolute bottom-32 left-12 w-1 h-1 rounded-full bg-gold-400/30" />

      <div className="relative z-10 w-full max-w-lg mx-auto text-center">
        {/* Badge Instagram */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#E1306C]/15 to-[#F77737]/15 border border-[#E1306C]/30 rounded-full mb-7">
          <i className="ri-instagram-line text-[#E1306C] text-sm" />
          <span className="text-[11px] font-bold text-white/70 tracking-[0.18em] uppercase">Exclusivo para seguidoras do Instagram</span>
        </div>

        {/* Foto + credencial */}
        <div className="flex items-center justify-center gap-3 mb-7">
          <div className="w-14 h-14 rounded-full ring-2 ring-gold-500/50 ring-offset-2 ring-offset-[#0d0c09] overflow-hidden shrink-0">
            <img
              src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/a68d70c6b66064870b0e9282c113321b.jpeg"
              alt="Bia Holmes"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="text-left">
            <p className="text-white font-semibold text-sm leading-tight">Bia Holmes</p>
            <p className="text-gold-400/80 text-[11px] font-medium">Especialista em PNL Clínica</p>
            <div className="flex items-center gap-1 mt-0.5">
              {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-gold-400 text-[9px]" />)}
              <span className="text-white/30 text-[9px] ml-1">4.200+ alunas</span>
            </div>
          </div>
        </div>

        {/* Headline principal */}
        <h1 className="font-display text-[2.1rem] sm:text-5xl font-bold text-white leading-[1.15] mb-5">
          Sua mente está<br />
          <span className="text-gold-400">sabotando seu próximo nível</span>
          <br />— e você ainda não sabe por quê
        </h1>

        <p className="text-base text-white/55 leading-relaxed max-w-md mx-auto mb-9">
          Descubra em 30 segundos qual padrão mental está travando seus resultados e receba o exercício de PNL exato para desbloqueá-lo. <strong className="text-white/80 font-semibold">Totalmente gratuito.</strong>
        </p>

        {/* CTAs */}
        <div className="flex flex-col gap-3 w-full max-w-xs mx-auto mb-10">
          <Link
            to="/diagnostico"
            onClick={onCtaClick}
            className="group flex items-center justify-center gap-2.5 w-full py-4 rounded-2xl bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-700 hover:to-gold-600 text-charcoal-900 font-bold text-base tracking-wide transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-brain-line text-lg" />
            Descobrir meu bloqueio grátis
            <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to="/exercicios-gratis"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/25 text-white/70 hover:text-white font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-sparkling-2-line text-gold-400 text-base" />
            Ver os 12 exercícios gratuitos
          </Link>
        </div>

        {/* Micro prova social */}
        <div className="flex items-center justify-center gap-1.5 mb-6">
          <div className="flex -space-x-2">
            {[
              'https://public.readdy.ai/ai/img_res/8775fffe7173f82e486ec189774b1b83.jpg',
              'https://readdy.ai/api/search-image?query=professional%20brazilian%20woman%20portrait%20soft%20natural%20warm%20background%20leader%20confident%20smile%2028%20years%20old%20headshot%20brunette&width=40&height=40&seq=ig-av2&orientation=squarish',
              'https://readdy.ai/api/search-image?query=professional%20brazilian%20woman%20portrait%20soft%20light%20natural%20background%20corporate%20confident%20smile%2035%20years%20old%20headshot%20blonde&width=40&height=40&seq=ig-av3&orientation=squarish',
            ].map((src, i) => (
              <div key={i} className="w-7 h-7 rounded-full overflow-hidden border-2 border-[#0d0c09]">
                <img src={src} alt="aluna" className="w-full h-full object-cover object-top" />
              </div>
            ))}
          </div>
          <p className="text-xs text-white/40 leading-tight">
            <strong className="text-white/65 font-semibold">+4.200 mulheres</strong> já transformaram suas vidas
          </p>
        </div>

        {/* Seta scroll */}
        <div className="flex flex-col items-center gap-1.5 mt-2">
          <span className="text-[10px] text-white/20 uppercase tracking-widest font-medium">Role para descobrir</span>
          <i className="ri-arrow-down-line text-white/20 text-base animate-bounce" />
        </div>
      </div>
    </section>
  );
}
